<template>
  <div>
    <!-- Desktop: hamburger shown when menu is collapsed -->
    <button v-if="!isVisible && !isMobile" @click="$emit('toggleMenu')" class="hamburger-btn">
      <i class="bi bi-list"></i>
    </button>

    <!-- Боковое меню с анимацией -->
    <transition name="slide" v-if="configData && configData.menu">
      <div class="side-menu" v-show="isVisible">

        <!-- Menu Header: Logo + Close button -->
        <div class="side-menu__header">
          <TextLogoComponent size="medium" link-to="/" />

          <button @click="$emit('toggleMenu')" class="side-menu__close-btn" :title="$t('menu.hide_menu')">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Scrollable Menu Content -->
        <div class="side-menu__content">

          <!-- ============ User Profile & Subscription (for all subscription services) ============ -->
          <template v-if="supportsSubscriptions">

            <!-- 1. User Profile Card (top, highlighted) -->
            <div class="user-profile-card" v-if="isAuthenticated && user">
              <div class="user-profile-info">
                <img v-if="user.photo_url" :src="user.photo_url" :alt="user.first_name" class="user-profile-avatar">
                <div v-else class="user-profile-avatar-placeholder">
                  <i class="bi bi-person-fill"></i>
                </div>
                <div class="user-profile-details">
                  <span class="user-profile-name">{{ user.first_name }}</span>
                  <span class="user-profile-username" v-if="user.username">@{{ user.username }}</span>
                </div>
              </div>
              <button class="user-profile-logout" @click="logout">
                <i class="bi bi-box-arrow-right"></i>
              </button>
            </div>

            <!-- 2. Telegram Auth (for unauthenticated users with subscription services) -->
            <telegram-auth class="mb-3" v-if="!configData.disable_telegram_auth && !isAuthenticated"></telegram-auth>

            <!-- 2. Subscription & Balance -->
            <div class="account-section" v-if="isAuthenticated">
              <!-- Subscription Row -->
              <router-link to="/subscription" class="account-row-compact" v-track="{ event: 'nav.link.click', target: 'subscription' }">
                <div class="account-row-left">
                  <i class="bi bi-star-fill account-icon subscription-icon"></i>
                  <span class="account-row-text">{{ subscriptionPlanName }}</span>
                  <span class="account-row-meta">{{ subscriptionUsage }}</span>
                </div>
                <i class="bi bi-chevron-right account-row-arrow"></i>
              </router-link>

              <!-- Balance Row (RU: рублёвый баланс + пополнение. BR — скрыто, оплата пакетами на /subscription) -->
              <div class="account-row-compact" v-if="!isBr">
                <div class="account-row-left">
                  <i class="bi bi-wallet2 account-icon balance-icon"></i>
                  <span class="account-row-text">{{ formattedBalance }} ₽</span>
                </div>
                <button class="topup-btn-small" @click="showTopupModal = true" v-track="{ event: 'billing.topup.click', target: 'side_menu' }">
                  {{ $t('subscription.topup') }}
                </button>
              </div>
            </div>

            <!-- Topup Modal (RU only) -->
            <TopupModal v-if="!isBr" v-model="showTopupModal" :chatbot-name="subscriptionChatbotName" />

            <!-- 3. MeetScribe Navigation -->
            <template v-if="isMeetScribe">
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.navigation') || 'Navigation' }}</span>
              </div>

              <router-link to="/meetings" class="menu-item" v-track="{ event: 'nav.link.click', target: 'meetings' }">
                <i class="bi bi-calendar-event"></i>
                <span>{{ $t('menu.meetings') || 'My Meetings' }}</span>
              </router-link>

              <!-- Developer Section -->
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.for_developers') }}</span>
              </div>

              <router-link to="/api-settings" class="menu-item" v-track="{ event: 'nav.link.click', target: 'api-settings' }">
                <i class="bi bi-speedometer2"></i>
                <span>Dashboard</span>
              </router-link>

              <router-link to="/api-docs" class="menu-item" v-track="{ event: 'nav.link.click', target: 'api-docs' }">
                <i class="bi bi-code-slash"></i>
                <span>API</span>
              </router-link>
            </template>

            <!-- 4. Zvonalitik Navigation -->
            <template v-if="isZvonalitik">
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.navigation') || 'Navigation' }}</span>
              </div>

              <router-link to="/zvonalitik" class="menu-item" v-track="{ event: 'nav.link.click', target: 'zvonalitik' }">
                <i class="bi bi-telephone"></i>
                <span>{{ $t('menu.calls') || 'My Calls' }}</span>
              </router-link>

              <router-link to="/knowledge-base" class="menu-item" v-track="{ event: 'nav.link.click', target: 'knowledge-base' }">
                <i class="bi bi-collection-play"></i>
                <span>{{ $t('menu.knowledge_base') || 'Call Samples' }}</span>
              </router-link>

              <router-link to="/amocrm/analytics" class="menu-item" v-if="isAuthenticated" v-track="{ event: 'nav.link.click', target: 'amocrm_analytics' }">
                <i class="bi bi-graph-up-arrow"></i>
                <span>{{ $t('menu.team_analytics') || 'Аналитика команды' }}</span>
              </router-link>

              <router-link to="/amocrm/settings" class="menu-item" v-if="isAuthenticated" v-track="{ event: 'nav.link.click', target: 'amocrm_settings' }">
                <i class="bi bi-gear"></i>
                <span>{{ $t('menu.settings') || 'Настройки' }}</span>
              </router-link>

              <!-- Developer Section -->
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.for_developers') }}</span>
              </div>

              <router-link to="/api-settings" class="menu-item" v-track="{ event: 'nav.link.click', target: 'api-settings' }">
                <i class="bi bi-key"></i>
                <span>{{ $t('menu.api_key') || 'API Key' }}</span>
              </router-link>

              <a href="https://zvonalitik.ru/developer/" target="_blank" class="menu-item">
                <i class="bi bi-code-slash"></i>
                <span>{{ $t('menu.api_docs') || 'Documentation' }}</span>
              </a>
            </template>

            <!-- 5. ClipCut Navigation -->
            <template v-if="isClipCut">
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.navigation') || 'Navigation' }}</span>
              </div>

              <router-link to="/materials" class="menu-item" v-track="{ event: 'nav.link.click', target: 'materials' }">
                <i class="bi bi-film"></i>
                <span>{{ $t('menu.my_shorts') || 'My Shorts' }}</span>
              </router-link>

              <!-- V2T-42 · M2: «Что нового», бейдж гаснет после просмотра -->
              <a href="#" class="menu-item" v-if="whatsNewHasEntries" @click.prevent="openWhatsNew" v-track="{ event: 'nav.link.click', target: 'whats_new' }">
                <i class="bi bi-stars"></i>
                <span>{{ $t('whats_new.title') }}</span>
                <span v-if="whatsNewUnseen" class="badge rounded-pill bg-danger ms-auto">{{ whatsNewUnseen }}</span>
              </a>
            </template>

            <!-- 6. Vid2Text Navigation -->
            <template v-if="!isMeetScribe && !isClipCut && !isZvonalitik">
              <div class="menu-section-divider">
                <span class="menu-section-title">{{ $t('menu.navigation') || 'Navigation' }}</span>
              </div>

              <router-link to="/materials" class="menu-item" v-track="{ event: 'nav.link.click', target: 'materials' }">
                <i class="bi bi-journal"></i>
                <span>{{ $t('menu.my_materials') }}</span>
              </router-link>
            </template>

          </template>

          <!-- ============ Other Services Layout (no subscription) ============ -->
          <template v-else>
            <router-link to="/" class="menu-item" v-if="configData.menu.main" v-track="{ event: 'nav.link.click', target: 'home' }">
              <i class="bi bi-house-door"></i>
              <span>{{ $t('menu.home') }}</span>
            </router-link>

            <router-link to="/history" class="menu-item" v-if="configData.menu.history" v-track="{ event: 'nav.link.click', target: 'history' }">
              <i class="bi bi-clock-history"></i>
              <span>{{ $t('menu.history') }}</span>
            </router-link>

            <router-link to="/materials/filter" class="menu-item" v-if="(configData.chatbot_name === 'chaitanyaacademy' || configData.chatbot_name === 'harikatha')" v-track="{ event: 'nav.link.click', target: 'materials_filter' }">
              <i class="bi bi-funnel"></i>
              <span>{{ $t('materials.search_placeholder') }}</span>
            </router-link>

            <router-link to="/materials/article-generator" class="menu-item" v-if="configData.chatbot_name === 'chaitanyaacademy' || configData.chatbot_name === 'harikatha'" v-track="{ event: 'nav.link.click', target: 'materials_article-generator' }">
              <i class="bi bi-chat-quote"></i>
              <span>{{ $t('menu.gurudev_says') }}</span>
            </router-link>

            <router-link to="/shlokas" class="menu-item" v-if="configData.is_moderator && (configData.chatbot_name === 'chaitanyaacademy' || configData.chatbot_name === 'harikatha')" v-track="{ event: 'nav.link.click', target: 'shlokas' }">
              <i class="bi bi-book"></i>
              <span>{{ $t('menu.manage_shlokas') }}</span>
            </router-link>

            <div v-if="!configData.block_data_management">
              <router-link to="/channels" class="menu-item" v-if="configData.menu.channels" v-track="{ event: 'nav.link.click', target: 'channels' }">
                <i class="bi bi-collection"></i>
                <span>{{ configData.menu.channels.caption }}</span>
              </router-link>

              <router-link to="/collections" class="menu-item" v-if="configData.menu.collections" v-track="{ event: 'nav.link.click', target: 'collections' }">
                <i class="bi bi-boxes"></i>
                <span>{{ configData.menu.collections.caption }}</span>
              </router-link>

              <router-link to="/materials" class="menu-item" v-if="configData.menu.materials" v-track="{ event: 'nav.link.click', target: 'materials' }">
                <i class="bi bi-journal"></i>
                <span>{{ configData.menu.materials.caption }}</span>
              </router-link>
            </div>
          </template>

          <!-- Upload File Button (для clipcut и vid2text) -->
          <UploadButton
            v-if="showUploadButton && isAuthenticated"
            btn-class="menu-item upload-file-btn"
            @click="showUploadModal = true"
          >
            <span>{{ $t('menu.upload_file') }}</span>
          </UploadButton>

          <!-- Ссылка вместо файла: сервер выкачает сам, человек не грузит гигабайты
               со своего канала. Поддерживаемые источники показывает само окно. -->
          <UploadButton
            v-if="showUploadButton && isAuthenticated"
            btn-class="menu-item upload-url-btn"
            icon="bi-link-45deg"
            @click="showUrlModal = true"
          >
            <span>{{ $t('menu.upload_url') }}</span>
          </UploadButton>

          <!-- Upload File Modal -->
          <UploadFileModal
            :show="showUploadModal"
            @close="showUploadModal = false"
            @uploaded="handleUploadSuccess"
          />

          <!-- Upload URL Modal -->
          <UploadUrlModal
            :show="showUrlModal"
            @close="showUrlModal = false"
            @uploaded="handleUploadSuccess"
          />

          <!-- Telegram Auth - show for unauthenticated users -->
          <telegram-auth class="mb-3" v-if="!configData.disable_telegram_auth && !isAuthenticated && !supportsSubscriptions"></telegram-auth>
        </div>

        <!-- Settings Footer: Language + Theme + Copyright -->
        <div class="side-menu__footer">
          <div class="settings-row">
            <!-- Language Switcher -->
            <div class="language-toggle">
              <button
                v-for="lang in availableLanguages"
                :key="lang.code"
                @click="setLanguage(lang.code)"
                :class="['lang-btn', { active: currentLocale === lang.code }]"
                :title="lang.label"
              >
                {{ lang.label }}
              </button>
            </div>
            <!-- Theme Toggle -->
            <button @click="toggleTheme" class="theme-toggle-btn" :title="darkMode ? $t('theme.switch_to_light') : $t('theme.switch_to_dark')">
              <i :class="darkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
            </button>
          </div>
          <!-- Copyright, Help & Support -->
          <div class="footer-info">
            <span class="copyright" v-html="configData.footer_text"></span>
            <div class="footer-links">
              <router-link to="/help" class="footer-link" v-track="{ event: 'nav.link.click', target: 'help' }">
                <i class="bi bi-question-circle"></i> {{ $t('menu.help') }}
              </router-link>
              <a :href="supportLink" target="_blank" rel="noopener" class="footer-link">
                <i class="bi bi-telegram"></i> {{ $t('menu.support') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <overlay-component :visible="isVisible && isMobile" @click="$emit('toggleMenu')"></overlay-component>
  </div>
</template>


<script>
import { computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { mapGetters, mapActions } from 'vuex';
import OverlayComponent from '@/components/OverlayComponent.vue';
import TextLogoComponent from '@/components/TextLogoComponent.vue';
import TopupModal from '@/components/subscription/TopupModal.vue';
import UploadFileModal from '@/components/UploadFileModal.vue';
import UploadUrlModal from '@/components/UploadUrlModal.vue';
import UploadButton from '@/components/UploadButton.vue';
import { whatsNewState, whatsNewEntries, whatsNewUnseenCount, openWhatsNew } from '@/lib/whatsNew';
import useClipboard from 'vue-clipboard3'
import { useToast } from "vue-toastification";
import TelegramAuth from './TelegramAuth.vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getAvailableLanguages, isBrTenant } from '../locales/tenantLocale';

import supportLinkMixin from '@/mixins/supportLinkMixin';

export default {
  mixins: [supportLinkMixin],
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    }
  },

  data(){
    return{
      menuExpanded: this.isVisible, // Управляет состоянием меню
      showTopupModal: false,
      showUploadModal: false,
      showUrlModal: false,
      whatsNewState
    }
  },

  components: {
    OverlayComponent,
    TelegramAuth,
    TextLogoComponent,
    TopupModal,
    UploadFileModal,
    UploadUrlModal,
    UploadButton
  },  

  watch: {
    // Следим за изменениями параметра isVisible
    isVisible(newVal) {
      this.menuExpanded = newVal;
    },
    // Quick Auth Links - auto-open upload modal
    // @see docs/features/QUICK_AUTH_LINKS.md
    pendingAction: {
      handler(action) {
        if (action === 'upload' && this.isAuthenticated) {
          this.showUploadModal = true;
          // Сбрасываем action после открытия модала
          this.$store.dispatch('app/setPendingAction', null);
        }
      },
      immediate: true // Проверяем сразу при монтировании
    }
  },  
  methods: {
    openWhatsNew() {
      openWhatsNew(this.configData, this.$i18n.locale);
      if (this.isMobile) this.$emit('toggleMenu');
    },
    ...mapActions('app', ['setIsMobile', 'toggleDarkMode']),

    toggleTheme() {
      this.toggleDarkMode();
    },

    handleUploadSuccess(data) {
      // Перенаправить на страницу материала
      if (data && data.materialId) {
        if (this.configData?.chatbot_name === 'meetscribe' && data.queueId) {
          // MeetScribe: use /meeting/{id}_{hash16} format
          const hash16 = String(data.materialId).substring(0, 16);
          this.$router.push(`/meeting/${data.queueId}_${hash16}`);
        } else {
          this.$router.push(`/article/${data.materialId}`);
        }
      }
      // Закрываем меню на мобильных
      if (this.isMobile) {
        this.$emit('toggleMenu');
      }
    },

  },
  mounted() {
    // Устанавливаем класс при монтировании компонента

    nextTick(() => {
      console.log('isMobile ', this.isMobile);
      console.log('configData in SideMenu:', this.configData);
      console.log('chatbot_name:', this.configData?.chatbot_name);
      if (this.isMobile){
        this.menuExpanded = false;
        console.log(this.menuExpanded);
      } else {
        this.menuExpanded = true;
      }


    });
  },
  computed: {
    ...mapGetters('app', ['isMobile', 'configData', 'darkMode', 'pendingAction']),
    ...mapGetters("article", ["getArticleItem"]),
    ...mapGetters("channel", ["getChannelItem"]),
    ...mapGetters("collection", ["getCollectionItem"]),

    whatsNewHasEntries() {
      return whatsNewEntries(this.configData, this.$i18n.locale).length > 0;
    },
    whatsNewUnseen() {
      // seenId — reactive: после просмотра бейдж пересчитывается сам
      void this.whatsNewState.seenId;
      return whatsNewUnseenCount(this.configData, this.$i18n.locale);
    },
    isClipCut() {
      return this.configData?.chatbot_name === 'clipcut' ||
             this.configData?.chatbot_name === 'clipcutglobal' ||
             this.configData?.chatbot_name === 'clipcutbr' ||
             this.configData?.service_name === 'clipcut' ||
             this.configData?.service_name === 'clipcutglobal';
    },


    isZvonalitik() {
      return this.configData?.chatbot_name === 'zvonalitik' ||
             this.configData?.service_name === 'zvonalitik';
    },

    isMeetScribe() {
      return this.configData?.chatbot_name === 'meetscribe' ||
             this.configData?.service_name === 'meetscribe';
    },

    articleItem: {
      get() {
        return this.getArticleItem || {};
      },
      set(value) {
        this.updateArticleItem(value);
      },
    },
    channelItem: {
      get() {
        return this.getChannelItem || {};
      },
      set(value) {
        this.updateChannelItem(value);
      },
    },
    collectionItem: {
      get() {
        return this.getCollectionItem || {};
      },
      set(value) {
        this.updateCollectionItem(value);
      },
    }
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const { locale } = useI18n();
    const article = computed(() => store.getters['article/getArticleItem']);
    const collection = computed(() => store.getters['collection/getCollectionItem']);
    const isAuthenticated = computed(() => store.getters['user/isAuthenticated']);
    const isFreePlan = computed(() => store.getters['subscription/isFreePlan']);
    const isBr = computed(() => isBrTenant());
    const getTopupUrl = computed(() => store.getters['subscription/getTopupUrl']);
    const getBalance = computed(() => store.getters['subscription/getBalance']);

    // Formatted balance for display
    const formattedBalance = computed(() => {
      const balance = getBalance.value || 0;
      return balance.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    });

    // Top-up URL with fallback
    const topupUrl = computed(() => {
      return getTopupUrl.value || 'https://bystropay.ru/payment.php?product=top_up_250&chatbot_name=meetscribe';
    });

    // Check if current service supports subscriptions
    const configData = computed(() => store.getters['app/configData']);
    const supportsSubscriptions = computed(() => {
      const serviceName = configData.value?.service_name;
      const chatbotName = configData.value?.chatbot_name;
      return ['meetscribe', 'clipcut', 'clipcutglobal', 'vid2text', 'zvonalitik'].includes(serviceName) ||
             ['meetscribe', 'clipcut', 'clipcutglobal', 'vid2text', 'zvonalitik'].includes(chatbotName);
    });

    // Get chatbot name for subscription API calls
    const subscriptionChatbotName = computed(() => {
      const chatbotName = configData.value?.chatbot_name;
      // Normalize clipcutglobal to clipcut for subscription purposes
      if (chatbotName === 'clipcutglobal') return 'clipcut';
      return chatbotName || 'meetscribe';
    });

    // Show upload button for clipcut and vid2text (not meetscribe - has its own upload in dashboard)
    const showUploadButton = computed(() => {
      const serviceName = configData.value?.service_name;
      const chatbotName = configData.value?.chatbot_name;
      // Show for clipcut, clipcutglobal, vid2text, narezkavideo
      // NOT for meetscribe (has its own upload in MeetingsDashboard)
      const uploadServices = ['clipcut', 'clipcutglobal', 'vid2text', 'narezkavideo'];
      if (uploadServices.includes(serviceName) || uploadServices.includes(chatbotName)) return true;
      // Show for harikatha/chaitanyaacademy moderators
      if (configData.value?.is_moderator && (serviceName === 'harikatha' || chatbotName === 'chaitanyaacademy')) return true;
      return false;
    });

    const { toClipboard } = useClipboard()

    const { t } = useI18n();

    const copy = async (text) => {
      try {
        await toClipboard(text)
        const toast = useToast();
        toast.success(t('article.toasts.copied_to_clipboard'), { timeout: 2000 });
      } catch (e) {
        console.error(e)
      }
    }

    const currentLocale = computed(() => locale.value);

    // Языки зависят от тенанта (домена): на BR — [PT, EN] без русского.
    const availableLanguages = getAvailableLanguages();

    const setLanguage = (lang) => {
      locale.value = lang;
      localStorage.setItem('user-locale', lang);
    };

    // User data for profile display
    const user = computed(() => store.getters['user/getUser']);

    // Subscription data for display
    const subscription = computed(() => store.getters['subscription/getSubscription']);
    const subscriptionPlanName = computed(() => {
      const sub = subscription.value;
      if (!sub || !sub.plan_name) return 'Free';
      return sub.plan_name;
    });
    const subscriptionUsage = computed(() => {
      const sub = subscription.value;
      if (!sub) return '';
      const used = sub.used_minutes || 0;
      const limit = sub.limit_minutes || 0;
      return `${used}/${limit} ${t('subscription.min')}`;
    });

    // Logout function
    const logout = () => {
      localStorage.removeItem('userData');
      localStorage.removeItem('authToken');
      store.dispatch('user/updateAuthentication', false);
      store.dispatch('user/updateUser', null);
    };

    return {
      article, collection, copy, isAuthenticated, route, currentLocale, setLanguage, availableLanguages, isFreePlan, topupUrl, formattedBalance,
      supportsSubscriptions, subscriptionChatbotName, showUploadButton, user, logout, subscriptionPlanName, subscriptionUsage, isBr
    };
  } 
};
</script>

<style scoped>
/* =====================================================
   Side Menu - Flexbox Layout with Scrollable Content
   ===================================================== */
.side-menu {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile */
  width: 300px;
  display: flex;
  flex-direction: column;
  background-color: var(--yt-bg-secondary, #f8f9fa);
  box-shadow: var(--yt-shadow-md, 2px 0 5px rgba(0, 0, 0, 0.1));
  z-index: 2;
  transition: transform 0.3s ease, background-color 0.3s ease;
  overflow: hidden;
}

.isMobile .side-menu {
  z-index: 101;
  width: 85vw;
  max-width: 320px;
}

/* Menu Header: Logo + Close Button */
.side-menu__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--yt-border-color, #e5e7eb);
}

.side-menu__logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.side-menu__logo img {
  max-height: 40px;
  max-width: 180px;
  object-fit: contain;
}

.side-menu__logo-placeholder {
  height: 40px;
}

.side-menu__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--yt-bg-tertiary, #f3f4f6);
  border-radius: 8px;
  color: var(--yt-text-secondary, #6b7280);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.side-menu__close-btn:hover {
  background-color: var(--yt-danger, #ef4444);
  color: white;
}

/* Scrollable Menu Content */
.side-menu__content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  -webkit-overflow-scrolling: touch;
}

/* Custom scrollbar for menu content */
.side-menu__content::-webkit-scrollbar {
  width: 6px;
}

.side-menu__content::-webkit-scrollbar-track {
  background: transparent;
}

.side-menu__content::-webkit-scrollbar-thumb {
  background-color: var(--yt-border-color, #d1d5db);
  border-radius: 3px;
}

.side-menu__content::-webkit-scrollbar-thumb:hover {
  background-color: var(--yt-text-muted, #9ca3af);
}

/* Menu Footer: Language, Theme, Copyright */
.side-menu__footer {
  flex-shrink: 0;
  padding: 16px 20px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  background-color: var(--yt-bg-secondary, #f8f9fa);
}

/* =====================================================
   Menu Items
   ===================================================== */
.side-menu .menu-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  margin-bottom: 8px;
  color: var(--yt-text-primary, #333);
  font-weight: 500;
  text-decoration: none;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  transition: background-color 0.2s, color 0.2s;
}

.side-menu .menu-item i {
  font-size: 20px;
  margin-right: 10px;
  color: var(--yt-text-secondary, #6c757d);
}

.side-menu .menu-item span {
  font-size: 15px;
}

.side-menu .menu-item:hover {
  background-color: var(--yt-hover-bg, #e7f3ff);
  color: var(--yt-primary, #007bff);
}

.side-menu .menu-item:hover i {
  color: var(--yt-primary, #007bff);
}

.side-menu .menu-item-active {
  background-color: var(--yt-active-bg, #e0f0ff);
  color: var(--yt-primary, #007bff);
}

/* Telegram Auth in menu */
.side-menu .telegram-auth {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  justify-content: center;
  margin-bottom: 8px;
  color: var(--yt-text-primary, #333);
  font-weight: 500;
  text-decoration: none;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  transition: background-color 0.2s, color 0.2s;
}

/* =====================================================
   Desktop Hamburger Button
   ===================================================== */
.hamburger-btn {
  position: fixed;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--yt-bg-card, #fff);
  border: 1px solid var(--yt-border-color, #e5e7eb);
  border-radius: 8px;
  font-size: 24px;
  color: var(--yt-text-primary, #1f2937);
  cursor: pointer;
  z-index: 48;
  box-shadow: var(--yt-shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.05));
  transition: all 0.2s ease;
}

.hamburger-btn:hover {
  background-color: var(--yt-hover-bg, #f3f4f6);
  border-color: var(--yt-primary, #4F46E5);
}

/* =====================================================
   Slide Animation
   ===================================================== */
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  border: 1px solid var(--yt-border-color, #e5e7eb);
}

.language-toggle {
  display: flex;
  gap: 4px;
}

.lang-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid var(--yt-border-color, #e5e7eb);
  background-color: transparent;
  color: var(--yt-text-secondary, #6b7280);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  background-color: var(--yt-hover-bg, #e7f3ff);
  color: var(--yt-primary, #4F46E5);
  border-color: var(--yt-primary, #4F46E5);
}

.lang-btn.active {
  background-color: var(--yt-primary, #4F46E5);
  color: white;
  border-color: var(--yt-primary, #4F46E5);
}

.theme-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--yt-bg-tertiary, #e9ecef);
  border-radius: 8px;
  color: var(--yt-text-secondary, #6b7280);
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme-toggle-btn:hover {
  background-color: var(--yt-primary, #4F46E5);
  color: white;
  transform: scale(1.05);
}

.theme-toggle-btn i {
  font-size: 18px;
}

/* Logo adaptation for dark theme */
.side-menu__logo img,
.side-menu .menu-item img {
  transition: filter 0.3s ease;
}

html[data-bs-theme="dark"] .side-menu__logo img,
html[data-bs-theme="dark"] .side-menu .menu-item img {
  filter: brightness(0) invert(1);
}

/* Menu section divider */
.menu-section-divider {
  margin: 15px 0 10px 0;
  padding: 0 15px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  padding-top: 15px;
}

.menu-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--yt-text-muted, #9ca3af);
}

/* Footer info (copyright + support) */
.footer-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  font-size: 12px;
  color: var(--yt-text-muted, #9ca3af);
}

.footer-info .copyright {
  text-align: center;
}

.footer-info .copyright :deep(a) {
  color: var(--yt-primary, #4F46E5);
  text-decoration: none;
}

.footer-info .copyright :deep(a:hover) {
  text-decoration: underline;
}

.footer-links {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.footer-info .footer-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--yt-text-muted, #9ca3af);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-info .footer-link:hover {
  color: var(--yt-primary, #4F46E5);
}

/* =====================================================
   User Profile Card (MeetScribe)
   ===================================================== */
.user-profile-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--yt-primary, #4F46E5) 0%, #7C3AED 100%);
  border-radius: 12px;
  color: white;
}

.user-profile-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.user-profile-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.user-profile-avatar-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-profile-avatar-placeholder i {
  font-size: 22px;
  color: rgba(255, 255, 255, 0.8);
}

.user-profile-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-profile-name {
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-profile-username {
  font-size: 13px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-profile-logout {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.user-profile-logout:hover {
  background: rgba(255, 255, 255, 0.25);
}

.user-profile-logout i {
  font-size: 16px;
}

/* =====================================================
   Account Section (Subscription & Balance)
   ===================================================== */
.account-section {
  background-color: var(--yt-bg-card, #fff);
  border-radius: 10px;
  border: 1px solid var(--yt-border-color, #e5e7eb);
  margin-bottom: 16px;
  overflow: hidden;
}

.account-row-compact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s;
}

.account-row-compact:not(:last-child) {
  border-bottom: 1px solid var(--yt-border-color, #e5e7eb);
}

.account-row-compact:hover {
  background-color: var(--yt-hover-bg, #f9fafb);
}

.account-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.account-icon {
  font-size: 18px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.subscription-icon {
  color: #F59E0B;
}

.balance-icon {
  color: #10B981;
}

.account-row-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--yt-text-primary, #1f2937);
}

.account-row-meta {
  font-size: 13px;
  color: var(--yt-text-muted, #9ca3af);
  margin-left: 6px;
}

.account-row-arrow {
  font-size: 14px;
  color: var(--yt-text-muted, #9ca3af);
}

.topup-btn-small {
  padding: 5px 10px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topup-btn-small:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

/* Dark mode */
html[data-bs-theme="dark"] .account-section {
  background-color: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4B5563);
}

html[data-bs-theme="dark"] .account-row-compact:not(:last-child) {
  border-color: var(--yt-border-color, #4B5563);
}

html[data-bs-theme="dark"] .account-row-compact:hover {
  background-color: var(--yt-bg-card, #1f2937);
}

html[data-bs-theme="dark"] .account-row-text {
  color: var(--yt-text-primary, #F9FAFB);
}

/* Upload file / upload URL buttons — две двери одного действия, вид общий */
.upload-file-btn,
.upload-url-btn {
  border: 1px dashed var(--yt-primary, #4F46E5) !important;
  background-color: rgba(79, 70, 229, 0.05) !important;
  color: var(--yt-primary, #4F46E5) !important;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.upload-file-btn i,
.upload-url-btn i {
  color: var(--yt-primary, #4F46E5) !important;
}

.upload-file-btn:hover,
.upload-url-btn:hover {
  background-color: rgba(79, 70, 229, 0.1) !important;
  border-style: solid !important;
}

html[data-bs-theme="dark"] .upload-file-btn,
html[data-bs-theme="dark"] .upload-url-btn {
  border-color: #818cf8 !important;
  background-color: rgba(129, 140, 248, 0.1) !important;
  color: #818cf8 !important;
}

html[data-bs-theme="dark"] .upload-file-btn i,
html[data-bs-theme="dark"] .upload-url-btn i {
  color: #818cf8 !important;
}

html[data-bs-theme="dark"] .upload-file-btn:hover,
html[data-bs-theme="dark"] .upload-url-btn:hover {
  background-color: rgba(129, 140, 248, 0.2) !important;
}

/* Вторая кнопка стоит вплотную к первой, как продолжение того же выбора */
.upload-url-btn { margin-top: 6px; }
</style>
